import { useCallback } from 'react';
import { pick, prop } from 'ramda';
import { useAppSelector } from 'js/store/hooks';
import { TFormField } from 'js/components/common/Form/types';

import * as helpers from '../helpers';
import * as actions from '../store/actions';
import { useDynamicReducerSelector, useDynamicReducerDispatch } from '../store';

export const useUpdateImsValues = () => {
    const dispatch = useDynamicReducerDispatch();
    const allowedCountries = useAppSelector(prop('allowedCountries'));
    const { fields, hiddenByRulesFields, hiddenFields } = useDynamicReducerSelector(pick(['fields', 'hiddenByRulesFields', 'hiddenFields']));

    return useCallback(({ values }: { values: Record<string, any> }) => {
        if (!fields.length) {
            return;
        }

        const visibleFields = fields.filter((field) => !hiddenByRulesFields.includes(field.name)
            && !hiddenFields.includes(field.name));
        const innerFields = visibleFields
            .flatMap(field => 'innerFields' in field ? Object.values(field.innerFields) : []) as TFormField[];

        const imsFieldsValues = visibleFields
            .filter((field) => {
                return 'imsField' in field
                    && !('doNotSendToIMS' in field && field?.doNotSendToIMS)
                    && !('usePlayerTags' in field && field?.usePlayerTags);
            })
            .reduce((accumulator, field) => {
                const value = helpers.fieldToImsValue(values, field, allowedCountries);
                const extraImsFields = 'extraImsFields' in field ? helpers.getExtraImsFields(field.extraImsFields, value) : {};

                return {
                    ...accumulator,
                    ...extraImsFields,
                    [field.imsField as string]: value
                };
            }, {});
        const innerImsFieldsValues = innerFields
            .filter((field) => field.imsField && !hiddenFields.includes(field.name))
            .reduce((accumulator, field) => {
                const value = helpers.fieldToImsValue(values, field, allowedCountries);
                const extraImsFields = 'extraImsFields' in field ? helpers.getExtraImsFields(field.extraImsFields, value) : {};

                return {
                    ...accumulator,
                    ...extraImsFields,
                    [field.imsField as string]: value
                };
            }, {});

        const imsValues = {
            ...imsFieldsValues,
            ...innerImsFieldsValues
        };

        dispatch(actions.updateImsValues(imsValues));
    }, [
        allowedCountries,
        dispatch,
        fields,
        hiddenByRulesFields,
        hiddenFields
    ]);
};
